import React, { createContext, useContext, useMemo, useRef } from "react";
import { useId } from "../../hooks/use-id";

/**
 * Contexto interno del TabPanel.
 * Guarda el valor actual, el callback para cambiarlo y utilidades
 * para generar ids accesibles entre tabs y panels.
 */
const TabPanelContext = createContext(null);

/**
 * Hook interno para obtener el contexto del TabPanel.
 * Lanza un error si se usa un componente fuera de <TabPanel.Root>.
 *
 * @returns {{
 *  value: string,
 *  onValueChange?: (value: string) => void,
 *  baseId: string
 * }}
 */
const useTabPanelContext = () => {
	const context = useContext(TabPanelContext);

	if (!context) {
		throw new Error("Los componentes de TabPanel deben usarse dentro de <TabPanel.Root>.");
	}

	return context;
};

/**
 * Convierte un valor cualquiera en una porción segura para id HTML.
 *
 * @param {string} value
 * @returns {string}
 */
const sanitizeForId = (value) => {
	return String(value).trim().replace(/\s+/g, "-").toLowerCase();
};

/**
 * Genera el id del tab a partir del id base del componente y del value.
 *
 * @param {string} baseId
 * @param {string} value
 * @returns {string}
 */
const getTabId = (baseId, value) => {
	return `${baseId}-tab-${sanitizeForId(value)}`;
};

/**
 * Genera el id del panel a partir del id base del componente y del value.
 *
 * @param {string} baseId
 * @param {string} value
 * @returns {string}
 */
const getPanelId = (baseId, value) => {
	return `${baseId}-panel-${sanitizeForId(value)}`;
};

/**
 * Componente raíz del sistema de tabs.
 * Provee contexto compartido para tabs y panels.
 *
 * Este componente no maneja estado interno deliberadamente.
 * La idea es que sea controlado desde afuera, para mantenerlo
 * flexible y predecible.
 *
 * @param {Object} props
 * @param {string} props.value
 * Valor actual activo del tab panel.
 *
 * @param {(value: string) => void} [props.onValueChange]
 * Función que se ejecuta cuando se solicita cambiar de tab.
 *
 * @param {React.ReactNode} props.children
 * Hijos del tab panel.
 *
 * @param {string} [props.className]
 * Clases CSS opcionales para el contenedor raíz.
 *
 * @returns {JSX.Element}
 */
const Root = ({ value, onValueChange, children, className }) => {
	const id = useId();

	const contextValue = useMemo(() => {
		return {
			value,
			onValueChange,
			baseId: `tab-panel-${sanitizeForId(id)}`,
		};
	}, [value, onValueChange, id]);

	return (
		<TabPanelContext.Provider value={contextValue}>
			<div className={className || "w-100"}>{children}</div>
		</TabPanelContext.Provider>
	);
};

/**
 * Contenedor de triggers del TabPanel.
 * Se renderiza con role="tablist".
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * Hijos, normalmente varios <TabPanel.Trigger>.
 *
 * @param {string} [props.className]
 * Clases CSS opcionales para el contenedor.
 *
 * @param {string} [props.ariaLabel]
 * Nombre accesible del grupo de tabs.
 * Útil cuando no existe un título visible que lo describa.
 *
 * @param {"horizontal" | "vertical"} [props.orientation="horizontal"]
 * Orientación del tablist. También afecta la navegación por teclado.
 *
 * @returns {JSX.Element}
 */
const List = ({ children, className, ariaLabel, orientation = "horizontal" }) => {
	return (
		<div
			role="tablist"
			aria-label={ariaLabel}
			aria-orientation={orientation}
			className={
				className ||
				"w-100 justify-content-center align-items-center d-flex flex-column flex-md-row"
			}
		>
			{children}
		</div>
	);
};

/**
 * Botón que representa una pestaña del TabPanel.
 *
 * Se encarga automáticamente de:
 * - role="tab"
 * - aria-selected
 * - aria-controls
 * - id
 * - tabIndex
 * - click para cambio de tab
 * - navegación con teclado
 *
 * La navegación soporta:
 * - Horizontal: ArrowLeft / ArrowRight
 * - Vertical: ArrowUp / ArrowDown
 * - Home: primer tab
 * - End: último tab
 *
 * @param {Object} props
 * @param {string} props.value
 * Identificador único de la pestaña. Debe coincidir con el value
 * del <TabPanel.Content> correspondiente.
 *
 * @param {React.ReactNode} props.children
 * Contenido visible del botón. Puede ser texto, iconos, spans, etc.
 *
 * @param {string} [props.className]
 * Clases CSS opcionales para el botón.
 *
 * @param {string} [props.name]
 * Nombre accesible opcional. Si se pasa, se usa como aria-label.
 * Si no se pasa, se usa el texto natural del contenido del botón.
 *
 * @param {boolean} [props.disabled=false]
 * Deshabilita la pestaña.
 *
 * @param {"horizontal" | "vertical"} [props.orientation="horizontal"]
 * Orientación esperada para la navegación con flechas.
 * Normalmente debería coincidir con la orientación del List.
 *
 * @param {(event: React.KeyboardEvent<HTMLButtonElement>) => void} [props.onKeyDown]
 * Callback adicional para keydown.
 *
 * @param {(event: React.MouseEvent<HTMLButtonElement>) => void} [props.onClick]
 * Callback adicional para click.
 *
 * @returns {JSX.Element}
 */
const Trigger = ({
	value: triggerValue,
	children,
	className,
	name,
	disabled = false,
	orientation = "horizontal",
	onKeyDown,
	onClick,
}) => {
	const { value, onValueChange, baseId } = useTabPanelContext();
	const isSelected = value === triggerValue;
	const buttonRef = useRef(null);

	const tabId = getTabId(baseId, triggerValue);
	const panelId = getPanelId(baseId, triggerValue);

	/**
	 * Busca todos los tabs hermanos dentro del mismo tablist.
	 *
	 * @returns {HTMLButtonElement[]}
	 */
	const getTabsInSameList = () => {
		const currentElement = buttonRef.current;
		if (!currentElement) return [];

		const tablist = currentElement.closest('[role="tablist"]');
		if (!tablist) return [];

		return Array.from(tablist.querySelectorAll('[role="tab"]')).filter(
			(element) => !element.hasAttribute("disabled"),
		);
	};

	/**
	 * Mueve el foco a un tab específico por índice dentro del tablist.
	 *
	 * @param {number} nextIndex
	 * @returns {void}
	 */
	const focusTabByIndex = (nextIndex) => {
		const tabs = getTabsInSameList();
		const nextTab = tabs[nextIndex];

		if (nextTab) {
			nextTab.focus();
			nextTab.click();
		}
	};

	/**
	 * Maneja la navegación por teclado entre tabs.
	 *
	 * @param {React.KeyboardEvent<HTMLButtonElement>} event
	 * @returns {void}
	 */
	const handleKeyDown = (event) => {
		onKeyDown && onKeyDown(event);

		if (event.defaultPrevented) return;

		const tabs = getTabsInSameList();
		const currentIndex = tabs.findIndex((tab) => tab === buttonRef.current);

		if (currentIndex === -1) return;

		const isHorizontal = orientation === "horizontal";
		const isVertical = orientation === "vertical";

		switch (event.key) {
			case "ArrowRight": {
				if (!isHorizontal) return;
				event.preventDefault();
				const nextIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
				focusTabByIndex(nextIndex);
				break;
			}

			case "ArrowLeft": {
				if (!isHorizontal) return;
				event.preventDefault();
				const nextIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
				focusTabByIndex(nextIndex);
				break;
			}

			case "ArrowDown": {
				if (!isVertical) return;
				event.preventDefault();
				const nextIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
				focusTabByIndex(nextIndex);
				break;
			}

			case "ArrowUp": {
				if (!isVertical) return;
				event.preventDefault();
				const nextIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
				focusTabByIndex(nextIndex);
				break;
			}

			case "Home": {
				event.preventDefault();
				focusTabByIndex(0);
				break;
			}

			case "End": {
				event.preventDefault();
				focusTabByIndex(tabs.length - 1);
				break;
			}

			default:
				break;
		}
	};

	/**
	 * Maneja el click del tab y solicita el cambio de valor.
	 *
	 * @param {React.MouseEvent<HTMLButtonElement>} event
	 * @returns {void}
	 */
	const handleClick = (event) => {
		onClick && onClick(event);

		if (event.defaultPrevented) return;
		if (disabled) return;

		onValueChange(triggerValue);
	};

	return (
		<button
			ref={buttonRef}
			type="button"
			role="tab"
			id={tabId}
			aria-selected={isSelected}
			aria-controls={panelId}
			tabIndex={isSelected ? 0 : -1}
			aria-label={name}
			className={className}
			disabled={disabled}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
		>
			{children}
		</button>
	);
};

/**
 * Panel de contenido asociado a un Trigger.
 *
 * Se vincula automáticamente con su tab mediante:
 * - role="tabpanel"
 * - id
 * - aria-labelledby
 *
 * Por defecto solo renderiza el panel activo.
 * Si forceMount es true, siempre se renderiza pero los inactivos
 * quedan ocultos con hidden.
 *
 * @param {Object} props
 * @param {string} props.value
 * Identificador del panel. Debe coincidir con el value
 * del Trigger correspondiente.
 *
 * @param {React.ReactNode} props.children
 * Contenido del panel.
 *
 * @param {string} [props.className]
 * Clases CSS opcionales del panel.
 *
 * @param {boolean} [props.forceMount=false]
 * Si es true, el panel siempre existe en el DOM y se oculta
 * cuando no está activo. Si es false, los paneles inactivos
 * no se renderizan.
 *
 * @param {boolean} [props.focusable=false]
 * Si es true, agrega tabIndex={0} al panel.
 * Útil cuando quieres que el panel pueda recibir foco directamente.
 *
 * @returns {JSX.Element | null}
 */
const Content = ({
	value: contentValue,
	children,
	className,
	forceMount = false,
	focusable = false,
}) => {
	const { value, baseId } = useTabPanelContext();
	const isSelected = value === contentValue;

	const tabId = getTabId(baseId, contentValue);
	const panelId = getPanelId(baseId, contentValue);

	if (!forceMount && !isSelected) {
		return null;
	}

	return (
		<section
			role="tabpanel"
			id={panelId}
			aria-labelledby={tabId}
			hidden={!isSelected}
			tabIndex={focusable ? 0 : undefined}
			className={className || "w-100"}
		>
			{children}
		</section>
	);
};

/**
 * API pública del sistema TabPanel.
 */
export const TabPanel = {
	Root,
	List,
	Trigger,
	Content,
};
